import React, { useEffect, useState } from 'react';
import { Container, Table, Button, Alert, Modal, Form, Card, Badge, Spinner, Row, Col } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import {
  fetchUsers,
  createUser,
  updateUser,
  deleteUser,
  setFilters,
  setPage,
  clearError
} from '../redux/slices/usersSlice';

interface UserFormData {
  username: string;
  email: string;
  password: string;
  role: string;
  first_name: string;
  last_name: string;
  phone: string;
  job_title: string;
  department: string;
  status: string;
}

const Users: React.FC = () => {
  const dispatch = useAppDispatch();
  const { users, pagination, filters, loading, error } = useAppSelector((state) => state.users);
  const organizationDomain = useAppSelector((state) => state.organization.organization?.domain);
  const currentUser = useAppSelector((state) => state.auth.user);

  const [showModal, setShowModal] = useState(false);
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [formData, setFormData] = useState<UserFormData>({
    username: '',
    email: '',
    password: '',
    role: 'editor',
    first_name: '',
    last_name: '',
    phone: '',
    job_title: '',
    department: '',
    status: 'active'
  });

  useEffect(() => {
    dispatch(fetchUsers({ 
      page: pagination.page,
      limit: pagination.limit,
      ...filters 
    }));
  }, [dispatch, pagination.page, filters]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  const handleShowModal = (user?: any) => {
    if (user) {
      setEditingUserId(user.id);
      setFormData({
        username: user.username,
        email: user.email,
        password: '',
        role: user.role,
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        job_title: user.job_title || '',
        department: user.department || '',
        status: user.status
      });
    } else {
      setEditingUserId(null);
      setFormData({
        username: '',
        email: organizationDomain ? `@${organizationDomain}` : '',
        password: '',
        role: 'editor',
        first_name: '',
        last_name: '',
        phone: '',
        job_title: '',
        department: '',
        status: 'active'
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingUserId) {
        const updateData: any = { ...formData };
        if (!updateData.password) {
          delete updateData.password;
        }
        await dispatch(updateUser({ id: editingUserId, data: updateData })).unwrap();
      } else {
        await dispatch(createUser(formData)).unwrap();
      }
      setShowModal(false);
      dispatch(fetchUsers({ 
        page: pagination.page,
        limit: pagination.limit,
        ...filters 
      }));
    } catch (err) {
      // Error is handled by Redux
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await dispatch(deleteUser(id)).unwrap();
        dispatch(fetchUsers({ 
          page: pagination.page,
          limit: pagination.limit,
          ...filters 
        }));
      } catch (err) {
        // Error is handled by Redux
      }
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    dispatch(setFilters({ [key]: value }));
    dispatch(setPage(1));
  };

  const formatDate = (date: string) => {
    return date ? new Date(date).toLocaleDateString() : '-';
  };

  const getStatusBadge = (status: string) => {
    const styles: any = {
      active: { bg: '#f6ffed', color: '#52c41a' },
      inactive: { bg: '#fff7e6', color: '#fa8c16' },
      suspended: { bg: '#fff1f0', color: '#ff4d4f' }
    };
    const style = styles[status] || styles.active;
    return (
      <Badge style={{
        backgroundColor: style.bg,
        color: style.color,
        fontWeight: 500,
        padding: '4px 10px',
        borderRadius: '4px',
        fontSize: '12px',
        textTransform: 'capitalize'
      }}>
        {status}
      </Badge>
    );
  };

  const getRoleBadge = (role: string) => {
    return role === 'admin' ? (
      <Badge style={{
        backgroundColor: '#fff1f0',
        color: '#cf1322',
        fontWeight: 500,
        padding: '4px 10px',
        borderRadius: '4px',
        fontSize: '12px'
      }}>
        Admin
      </Badge>
    ) : (
      <Badge style={{
        backgroundColor: '#e6f4ff',
        color: '#0958d9',
        fontWeight: 500,
        padding: '4px 10px',
        borderRadius: '4px',
        fontSize: '12px'
      }}>
        {role === 'editor' ? 'Editor' : 'Viewer'}
      </Badge>
    );
  };

  const isLoopAdmin = currentUser?.email?.toLowerCase().endsWith('@loop.com.na') && currentUser?.role === 'admin';

  return (
    <div style={{ backgroundColor: '#f5f5f5', minHeight: '100vh', paddingTop: '2rem', paddingBottom: '2rem' }}>
      <Container>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{
            color: '#00579a',
            fontWeight: 600,
            fontSize: '28px',
            margin: 0
          }}>
            Users Management
          </h1>
          <div className="d-flex gap-2">
            {isLoopAdmin && (
              <Button
                onClick={() => window.location.href = '/organization/create'}
                style={{
                  backgroundColor: '#52c41a',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 20px',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(82,196,26,0.2)',
                  marginRight: '8px'
                }}
              >
                🏢 Create Organization
              </Button>
            )}
            <Button
              onClick={() => handleShowModal()}
              style={{
                backgroundColor: '#00579a',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '8px 20px',
                fontWeight: 500,
                boxShadow: '0 2px 4px rgba(0,87,154,0.2)'
              }}
            >
              + Add New User
            </Button>
          </div>
        </div>

        {error && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => dispatch(clearError())}
            style={{
              borderRadius: '6px',
              border: '1px solid #ffccc7',
              backgroundColor: '#fff2f0'
            }}
          >
            {error}
          </Alert>
        )}

        {isLoopAdmin && (
          <Alert 
            variant="success" 
            style={{
              borderRadius: '6px',
              border: '1px solid #b7eb8f',
              backgroundColor: '#f6ffed',
              marginBottom: '1.5rem'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '20px' }}>🏢</span>
              <div>
                <strong>Loop Administrator</strong>
                <div style={{ fontSize: '14px', marginTop: '4px' }}>
                  As a Loop admin, you can create new organizations. Click the "Create Organization" button above to get started.
                </div>
              </div>
            </div>
          </Alert>
        )}

        {/* Filters */}
        <Card style={{
          border: 'none',
          borderRadius: '8px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.03), 0 1px 6px rgba(0,0,0,0.06)',
          marginBottom: '16px'
        }}>
          <Card.Body>
            <Row>
              <Col md={4}>
                <Form.Group>
                  <Form.Label style={{ fontSize: '14px', fontWeight: 500 }}>Search</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Search by name, username, or email..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    style={{
                      borderRadius: '6px',
                      border: '1px solid #d9d9d9',
                      fontSize: '14px'
                    }}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label style={{ fontSize: '14px', fontWeight: 500 }}>Status</Form.Label>
                  <Form.Select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    style={{
                      borderRadius: '6px',
                      border: '1px solid #d9d9d9',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">All Statuses</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label style={{ fontSize: '14px', fontWeight: 500 }}>Role</Form.Label>
                  <Form.Select
                    value={filters.role}
                    onChange={(e) => handleFilterChange('role', e.target.value)}
                    style={{
                      borderRadius: '6px',
                      border: '1px solid #d9d9d9',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">All Roles</option>
                    <option value="admin">Admin</option>
                    <option value="editor">Editor</option>
                    <option value="viewer">Viewer</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card style={{
          border: 'none',
          borderRadius: '8px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.03), 0 1px 6px rgba(0,0,0,0.06)',
          overflow: 'hidden'
        }}>
          <Card.Body style={{ padding: 0 }}>
            {loading ? (
              <div className="text-center" style={{ padding: '60px 0' }}>
                <Spinner
                  animation="border"
                  style={{ color: '#00579a', width: '40px', height: '40px' }}
                />
                <p style={{ marginTop: '16px', color: '#8c8c8c' }}>Loading users...</p>
              </div>
            ) : (
              <>
                <div style={{ overflowX: 'auto' }}>
                  <Table
                    hover
                    responsive
                    style={{
                      margin: 0,
                      borderCollapse: 'separate',
                      borderSpacing: 0
                    }}
                  >
                    <thead>
                      <tr style={{ backgroundColor: '#fafafa' }}>
                        <th style={headerStyle}>User</th>
                        <th style={headerStyle}>Contact</th>
                        <th style={headerStyle}>Professional</th>
                        <th style={headerStyle}>Role</th>
                        <th style={headerStyle}>Status</th>
                        <th style={headerStyle}>Last Login</th>
                        <th style={headerStyle}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="text-center" style={{
                            padding: '60px 20px',
                            color: '#8c8c8c',
                            fontSize: '15px'
                          }}>
                            No users found. Try adjusting your filters.
                          </td>
                        </tr>
                      ) : (
                        users.map((user) => (
                          <tr key={user.id}>
                            <td style={cellStyle}>
                              <div>
                                <div style={{ fontWeight: 600, color: '#262626', marginBottom: '2px' }}>
                                  {user.first_name && user.last_name
                                    ? `${user.first_name} ${user.last_name}`
                                    : user.username}
                                </div>
                                <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                                  @{user.username}
                                </div>
                              </div>
                            </td>
                            <td style={cellStyle}>
                              <div style={{ fontSize: '14px' }}>
                                <div>{user.email}</div>
                                {user.phone && (
                                  <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                                    {user.phone}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td style={cellStyle}>
                              {user.job_title ? (
                                <div style={{ fontSize: '14px' }}>
                                  <div style={{ fontWeight: 500 }}>{user.job_title}</div>
                                  {user.department && (
                                    <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                                      {user.department}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span style={{ color: '#d9d9d9' }}>—</span>
                              )}
                            </td>
                            <td style={cellStyle}>{getRoleBadge(user.role)}</td>
                            <td style={cellStyle}>{getStatusBadge(user.status)}</td>
                            <td style={cellStyle}>
                              {user.last_login ? formatDate(user.last_login) : (
                                <span style={{ color: '#8c8c8c' }}>Never</span>
                              )}
                            </td>
                            <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>
                              <Button
                                size="sm"
                                className="me-2"
                                onClick={() => handleShowModal(user)}
                                style={{
                                  backgroundColor: 'white',
                                  color: '#00579a',
                                  border: '1px solid #00579a',
                                  borderRadius: '4px',
                                  padding: '4px 12px',
                                  fontSize: '13px',
                                  fontWeight: 500
                                }}
                              >
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleDelete(user.id)}
                                style={{
                                  backgroundColor: 'white',
                                  color: '#ff4d4f',
                                  border: '1px solid #ff4d4f',
                                  borderRadius: '4px',
                                  padding: '4px 12px',
                                  fontSize: '13px',
                                  fontWeight: 500
                                }}
                              >
                                Delete
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                  <div className="d-flex justify-content-between align-items-center" style={{ padding: '16px 24px', borderTop: '1px solid #f0f0f0' }}>
                    <div style={{ fontSize: '14px', color: '#595959' }}>
                      Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} users
                    </div>
                    <div>
                      <Button
                        size="sm"
                        disabled={pagination.page === 1}
                        onClick={() => dispatch(setPage(pagination.page - 1))}
                        style={{
                          marginRight: '8px',
                          backgroundColor: 'white',
                          color: '#00579a',
                          border: '1px solid #d9d9d9'
                        }}
                      >
                        Previous
                      </Button>
                      <Button
                        size="sm"
                        disabled={pagination.page === pagination.totalPages}
                        onClick={() => dispatch(setPage(pagination.page + 1))}
                        style={{
                          backgroundColor: 'white',
                          color: '#00579a',
                          border: '1px solid #d9d9d9'
                        }}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </Card.Body>
        </Card>
      </Container>

      {/* User Form Modal */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        size="lg"
      >
        <Modal.Header
          closeButton
          style={{
            borderBottom: '1px solid #f0f0f0',
            padding: '20px 24px'
          }}
        >
          <Modal.Title style={{
            color: '#00579a',
            fontWeight: 600,
            fontSize: '20px'
          }}>
            {editingUserId ? 'Edit User' : 'New User'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body style={{ padding: '24px' }}>
            {organizationDomain && !editingUserId && (
              <Alert variant="info" style={{ marginBottom: '20px', fontSize: '14px' }}>
                Email must use domain: <strong>@{organizationDomain}</strong>
              </Alert>
            )}
            <Row>
              <Col md={6}>
                <h6 style={{ color: '#00579a', fontWeight: 600, marginBottom: '16px' }}>Account Information</h6>
                <Form.Group className="mb-3">
                  <Form.Label style={labelStyle}>Username *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    required
                    style={inputStyle}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label style={labelStyle}>Email *</Form.Label>
                  <Form.Control
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    style={inputStyle}
                    placeholder={organizationDomain ? `user@${organizationDomain}` : ''}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label style={labelStyle}>
                    Password {editingUserId && <span style={{ color: '#8c8c8c', fontWeight: 400 }}>(leave blank to keep current)</span>}
                  </Form.Label>
                  <Form.Control
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={!editingUserId}
                    style={inputStyle}
                    placeholder={editingUserId ? "Leave blank to keep current password" : "Minimum 8 characters"}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label style={labelStyle}>Role *</Form.Label>
                  <Form.Select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    style={inputStyle}
                  >
                    <option value="viewer">Viewer</option>
                    <option value="editor">Editor</option>
                    <option value="admin">Admin</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label style={labelStyle}>Status *</Form.Label>
                  <Form.Select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    style={inputStyle}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <h6 style={{ color: '#00579a', fontWeight: 600, marginBottom: '16px' }}>Personal & Professional Details</h6>
                <Form.Group className="mb-3">
                  <Form.Label style={labelStyle}>First Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    style={inputStyle}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label style={labelStyle}>Last Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    style={inputStyle}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label style={labelStyle}>Phone</Form.Label>
                  <Form.Control
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    style={inputStyle}
                    placeholder="+264 81 234 5678"
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label style={labelStyle}>Job Title</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.job_title}
                    onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                    style={inputStyle}
                    placeholder="e.g., Software Engineer"
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label style={labelStyle}>Department</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    style={inputStyle}
                    placeholder="e.g., IT Department"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer style={{
            borderTop: '1px solid #f0f0f0',
            padding: '16px 24px'
          }}>
            <Button
              variant="secondary"
              onClick={() => setShowModal(false)}
              style={{
                backgroundColor: 'white',
                color: '#595959',
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                padding: '8px 20px',
                fontWeight: 500
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={loading}
              style={{
                backgroundColor: '#00579a',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '8px 20px',
                fontWeight: 500,
                boxShadow: '0 2px 4px rgba(0,87,154,0.2)'
              }}
            >
              {loading ? 'Saving...' : (editingUserId ? 'Update User' : 'Create User')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

const headerStyle: React.CSSProperties = {
  padding: '16px',
  fontSize: '14px',
  fontWeight: 600,
  color: '#595959',
  borderBottom: '1px solid #f0f0f0',
  backgroundColor: '#fafafa',
  whiteSpace: 'nowrap'
};

const cellStyle: React.CSSProperties = {
  padding: '16px',
  fontSize: '14px',
  color: '#595959',
  borderBottom: '1px solid #f0f0f0',
  verticalAlign: 'middle'
};

const labelStyle: React.CSSProperties = {
  fontWeight: 500,
  color: '#262626',
  fontSize: '14px',
  marginBottom: '8px'
};

const inputStyle: React.CSSProperties = {
  borderRadius: '6px',
  border: '1px solid #d9d9d9',
  padding: '8px 12px',
  fontSize: '14px'
};

export default Users;